/**
 * As cotas e os tetos do contrato, do lado da tela (`B-03`, `A-09`, ADR-070).
 *
 * Quem define é o dono do banco; a tela só lê `GET /admin/cotas` — e só o administrador, que é
 * quem conversa sobre o contrato (decisão de 16/10). O aviso aparece a partir de 80% de uma cota
 * e quando ela acaba; o número vem do servidor, truncado (99,9% é 99%), e aqui só ganha a frase.
 */
import type { CotaNaTela, TetosDoContrato } from "./api/tipos";
import type { Significado } from "./componentes/base";
import { numeroLongo } from "./formato";

/** De quanto em quanto tempo a moldura do administrador relê as cotas. */
export const RELEITURA_DAS_COTAS_MS = 10 * 60 * 1000;

/** `01/11/2026`, sem passar por fuso: a data é a de Brasília, como veio. */
export function dataDaCota(iso: string): string {
  const [ano, mes, dia] = iso.split("-");
  return `${dia}/${mes}/${ano}`;
}

const PLURAL: Record<string, string> = { chamada: "chamadas", token: "tokens" };

/** "850 de 1.000 chamadas" — com o que está na fila somado ao usado. */
export function usoDaCota(c: CotaNaTela): string {
  const unidade = PLURAL[c.unidade] ?? c.unidade;
  return `${numeroLongo(c.usado + c.reservado)} de ${numeroLongo(c.teto)} ${unidade}`;
}

export const COR_DA_COTA: Record<CotaNaTela["situacao"], Significado> = {
  livre: "verde",
  aviso: "amarelo",
  esgotada: "vermelho",
};

export interface AvisoDaCota {
  cor: "amarelo" | "vermelho";
  frase: string;
}

/**
 * A faixa do administrador: vermelha quando uma cota acabou (o que usa a capacidade está sendo
 * recusado), âmbar a partir de 80%. Nula quando não há o que dizer.
 */
export function avisoDaCota(
  tetos: TetosDoContrato | null | undefined,
): AvisoDaCota | null {
  if (!tetos?.aviso) return null;
  const renova = dataDaCota(tetos.renova_em);
  const lista = (cs: CotaNaTela[]) =>
    cs.map((c) => `${c.rotulo} (${usoDaCota(c)})`).join(", ");
  const esgotadas = tetos.cotas.filter((c) => c.situacao === "esgotada");
  if (esgotadas.length > 0) {
    return {
      cor: "vermelho",
      frase:
        `A cota deste mês acabou: ${lista(esgotadas)}. O que usa essa capacidade está ` +
        `sendo recusado até ${renova}. Para ampliar, fale com a IA Solutions.`,
    };
  }
  const perto = tetos.cotas.filter((c) => c.situacao === "aviso");
  if (perto.length === 0) return null;
  return {
    cor: "amarelo",
    frase: `A cota deste mês está perto do fim: ${lista(perto)}. Renova em ${renova}.`,
  };
}
